import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Alert } from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import API from "../../services/api";
import { t } from "@lingui/macro";
import { getLocalizedField } from "../../i18n/backendTranslations";

const TicketForm = ({ onSubmit }) => {
  const { userEmail, isLoggedIn } = useAuth();
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promotionInfo, setPromotionInfo] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await API.get("api/tickets/public/prices");
        if (Array.isArray(response.data)) {
          console.log(t`Received prices:`, response.data);
          setPrices(response.data);

          setPromotionInfo(
            response.data.map((price) => {
              if (price.freeItemPromoActive && price.priceCategory !== "Free") {
                return {
                  priceId: price.id,
                  promoMessage: t`Buy ${price.quantityForFreeItem} tickets, get 1 free!`,
                };
              }
              return { priceId: price.id, promoMessage: "" };
            })
          );
        } else {
          console.error(t`Unexpected data format:`, response.data);
          setPrices([]);
        }
      } catch (error) {
        console.error(t`Error fetching prices:`, error);
        setPrices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPrices();
  }, []);

  const calculateFreeTickets = (tickets) => {
    Object.values(tickets).forEach((ticket) => {
      if (ticket.priceId === 14) {
        const freeCount = Object.values(tickets)
          .filter((t) => t.quantityForFreeItem > 0 && t.priceId !== 14)
          .reduce((sum, t) => sum + Math.floor(t.quantity / t.quantityForFreeItem), 0);
        ticket.quantity = freeCount;
      }
    });
  };

  const calculateTotal = (tickets) => {
    let totalAmount = 0;
    Object.values(tickets).forEach((ticket) => {
      const price = prices.find((p) => p.id === ticket.priceId);
      if (price) {
        totalAmount += ticket.quantity * price.finalPrice;
      }
    });
    return totalAmount;
  };

  const handleSubmit = (values) => {
    console.log(t`Form submitted with values:`, values);

    calculateFreeTickets(values.tickets);

    const calculatedTotal = calculateTotal(values.tickets);

    const ticketOrderRequest = {
      email: values.email,
      isGroup: values.isGroup,
      paymentMethod: "stripe",
      total: calculatedTotal,
      tickets: Object.values(values.tickets)
        .filter((ticket) => ticket.quantity > 0)
        .map((ticket) => ({
          priceId: ticket.priceId,
          priceCategory: ticket.priceCategory,
          quantity: ticket.quantity,
        })),
    };

    console.log(t`Ticket Order Data:`, ticketOrderRequest);
    onSubmit(ticketOrderRequest);
  };

  const initialValues = {
    email: isLoggedIn ? userEmail : "",
    isGroup: false,
    tickets: prices.reduce((acc, price) => {
      acc[price.id] = {
        priceId: price.id,
        priceCategory: price.priceCategory,
        quantity: 0,
        quantityForFreeItem: price.quantityForFreeItem || 0,
      };
      return acc;
    }, {}),
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t`Invalid email`)
      .required(t`Email is required`),
    tickets: Yup.object()
      .shape(
        Object.keys(initialValues.tickets).reduce((acc, key) => {
          acc[key] = Yup.object().shape({
            quantity: Yup.number()
              .integer()
              .min(0, t`Minimum quantity is 0`)
              .test(
                "excursion-quantity-check",
                t`Quantity must be 0 or at least 20 for Excursion tickets`,
                function (value) {
                  const { priceCategory } = this.parent;
                  if (priceCategory === "Excursion") {
                    return value === 0 || value >= 20;
                  }
                  return true;
                }
              )
              .required(t`Quantity is required`),
          });
          return acc;
        }, {})
      )
      .test(
        "at-least-one-ticket",
        t`At least one ticket must be selected`,
        (tickets) => {
          const totalQuantity = Object.values(tickets).reduce(
            (sum, ticket) => sum + ticket.quantity,
            0
          );
          return totalQuantity >= 1;
        }
      )
      .required(),
  });

  return (
    <div className="form-container">
      {isSuccess ? (
        <div className="success-message-container">
          <Alert variant="success" className="success-message">
            {successMessage}
          </Alert>
        </div>
      ) : (
        <>
          {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
          {loading ? (
            <div>{t`Loading...`}</div>
          ) : (
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ setFieldValue, values }) => {
                calculateFreeTickets(values.tickets);
                const total = calculateTotal(values.tickets);

                return (
                  <Form>
                    <label className="form-label" htmlFor="email">
                      {t`Email`} <span className="required">*</span>
                    </label>
                    <Field
                      type="email"
                      id="email"
                      name="email"
                      className="form-control"
                      placeholder={t`Enter your email`}
                    />
                    <ErrorMessage name="email" component="div" className="error-message" />

                    <div className="group-ticket-container">
                      <label className="form-label" htmlFor="isGroup">
                        {t`Group ticket`}
                      </label>
                      <Field type="checkbox" id="isGroup" name="isGroup" />
                      <span className="group-ticket-info">
                        {t`Places all guests on one ticket`}
                      </span>
                    </div>

                    <h2>{t`Tickets`}</h2>

                    {Object.entries(prices)
                      .filter(([key, price]) => price.id !== 14)
                      .map(([key, price]) => {
                        const promoMessage =
                          promotionInfo.find((promo) => promo.priceId === price.id)
                            ?.promoMessage || "";

                        return (
                          <div key={price.id} className="ticket-row">
                            <div className="ticket-header">
                              <h3 className="ticket-category">
   {getLocalizedField(price, "priceCategory")} 
</h3>
                              <span className="ticket-price">
                                {price.hasPromotion ? (
                                  <>
                                    <span
                                      style={{
                                        textDecoration: "line-through",
                                        marginRight: "10px",
                                      }}
                                    >
                                      {price.originalPrice.toFixed(2)} {price.currency}
                                    </span>
                                    <span style={{ color: "red" }}>
                                      {price.finalPrice.toFixed(2)} {price.currency}
                                    </span>
                                    {promoMessage && (
                                      <div className="promotion-info">
                                        <strong>{promoMessage}</strong>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <span>
                                    {price.originalPrice} {price.currency}
                                  </span>
                                )}
                              </span>
                            </div>

                            <div className="quantity-container">
                              <Field
                                type="number"
                                name={`tickets.${price.id}.quantity`}
                                className="form-control small-field"
                                placeholder="0"
                                onChange={(e) => {
                                  const value = parseInt(e.target.value, 10) || 0;
                                  setFieldValue(`tickets.${price.id}.quantity`, value);
                                }}
                                min="0"
                              />
                              <ErrorMessage
                                name={`tickets.${price.id}.quantity`}
                                component="div"
                                className="error-message"
                              />
                            </div>
                          </div>
                        );
                      })}

                    {Object.entries(values.tickets)
                      .filter(([key, ticket]) => ticket.priceId === 14 && ticket.quantity > 0)
                      .map(([key, ticket]) => (
                        <div key="14" className="ticket-row">
                          <div className="ticket-header">
                            <h3 className="ticket-category">{t`Free Ticket`}</h3>
                          </div>
                          <div className="free-ticket-container">
                            <Field
                              type="number"
                              name={`tickets.${key}.quantity`}
                              className="form-control small-field"
                              value={ticket.quantity}
                              readOnly
                            />
                          </div>
                        </div>
                      ))}

                    <div className="total-price">
                      <p>
                        {t`Total`}: {total} MXN
                      </p>
                    </div>

                    <div className="form-actions">
                      <button type="submit" className="add-new-btn">
                        {t`Purchase Tickets`}
                      </button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          )}
        </>
      )}
    </div>
  );
};

export default TicketForm;
